import React from 'react';
import { ArrowUpIcon, ArrowDownIcon, EyeIcon, EditIcon, CheckIcon, XIcon, FlagIcon, BuildingIcon, TrashIcon } from 'lucide-react';
import { ExchangeRate } from '../CurrencyExchangeView';
import { ExchangeRateBadge } from './ExchangeRateBadge';
interface ExchangeRateTableProps {
  rates: ExchangeRate[];
  onEdit: (rate: ExchangeRate) => void;
  onToggleStatus: (rateId: string) => void;
  onViewHistory: (rate: ExchangeRate) => void;
  onDelete?: (rateId: string) => void;
  locale: string;
  showOrgUnit?: boolean;
}
export function ExchangeRateTable({
  rates,
  onEdit,
  onToggleStatus,
  onViewHistory,
  onDelete,
  locale,
  showOrgUnit = false
}: ExchangeRateTableProps) {
  // Format date based on locale
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    }).format(date);
  };
  // Get flag emoji for currency
  const getCurrencyFlag = (currencyCode: string) => {
    const flagMap: Record<string, string> = {
      USD: '🇺🇸',
      EUR: '🇪🇺',
      GBP: '🇬🇧',
      CAD: '🇨🇦',
      AUD: '🇦🇺',
      JPY: '🇯🇵',
      CNY: '🇨🇳',
      INR: '🇮🇳',
      BRL: '🇧🇷',
      MXN: '🇲🇽',
      ZAR: '🇿🇦',
      RUB: '🇷🇺',
      SGD: '🇸🇬'
    };
    return flagMap[currencyCode] || '🌐';
  };
  return <div className="overflow-x-auto" data-id="element-9757">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-9758">
        <thead className="bg-gray-50" data-id="element-9759">
          <tr data-id="element-9760">
            {showOrgUnit && <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9761">
                Organisation Unit
              </th>}
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9762">
              Currency Pair
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9763">
              Rate
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9764">
              Type
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9765">
              Last Updated
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9766">
              Status
            </th>
            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9767">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-9768">
          {rates.length === 0 ? <tr data-id="element-9769">
              <td colSpan={showOrgUnit ? 7 : 6} className="px-6 py-4 text-center text-gray-500" data-id="element-9770">
                No exchange rates found matching your criteria.
              </td>
            </tr> : rates.map(rate => <tr key={rate.id} className={!rate.isActive ? 'bg-gray-50' : undefined} data-id="element-9771">
                {showOrgUnit && <td className="px-6 py-4" data-id="element-9772">
                    <div className="text-sm font-medium text-gray-900" data-id="element-9773">
                      {rate.orgUnitName}
                    </div>
                    {rate.orgUnitPath && <div className="text-sm text-gray-500" data-id="element-9774">
                        {rate.orgUnitPath}
                      </div>}
                  </td>}
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-9775">
                  <div className="flex items-center" data-id="element-9776">
                    <span className="mr-1" data-id="element-9777">
                      {getCurrencyFlag(rate.fromCurrency)}
                    </span>
                    <span className="font-medium" data-id="element-9778">{rate.fromCurrency}</span>
                    <ArrowRightIcon className="h-4 w-4 mx-1 text-gray-400" data-id="element-9779" />
                    <span className="mr-1" data-id="element-9780">
                      {getCurrencyFlag(rate.toCurrency)}
                    </span>
                    <span className="font-medium" data-id="element-9781">{rate.toCurrency}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-9782">
                  <div className="text-sm" data-id="element-9783">
                    <div className="font-medium text-gray-900" data-id="element-9784">
                      1 {rate.fromCurrency} = {rate.rate.toFixed(4)}{' '}
                      {rate.toCurrency}
                    </div>
                    <div className="text-gray-500 text-xs" data-id="element-9785">
                      1 {rate.toCurrency} = {rate.inverseRate.toFixed(4)}{' '}
                      {rate.fromCurrency}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-9786">
                  <ExchangeRateBadge type={rate.type} data-id="element-9787" />
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-9788">
                  <div className="text-sm text-gray-900" data-id="element-9789">
                    {formatDate(rate.lastUpdated)}
                  </div>
                  {rate.updatedBy && <div className="text-xs text-gray-500" data-id="element-9790">
                      {rate.updatedBy}
                    </div>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-9791">
                  {rate.isActive ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-9792">
                      <CheckIcon className="h-3 w-3 mr-1" data-id="element-9793" />
                      Active
                    </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-9794">
                      <XIcon className="h-3 w-3 mr-1" data-id="element-9795" />
                      Inactive
                    </span>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-9796">
                  <div className="flex justify-end space-x-2" data-id="element-9797">
                    <button onClick={() => onViewHistory(rate)} className="text-indigo-600 hover:text-indigo-900" title="View History" data-id="element-9798">
                      <EyeIcon className="h-4 w-4" data-id="element-9799" />
                    </button>
                    <button onClick={() => onEdit(rate)} className="text-blue-600 hover:text-blue-900" title="Edit" data-id="element-9800">
                      <EditIcon className="h-4 w-4" data-id="element-9801" />
                    </button>
                    <button onClick={() => onToggleStatus(rate.id)} className={rate.isActive ? 'text-amber-600 hover:text-amber-900' : 'text-green-600 hover:text-green-900'} title={rate.isActive ? 'Deactivate' : 'Activate'} data-id="element-9802">
                      {rate.isActive ? <XIcon className="h-4 w-4" data-id="element-9803" /> : <CheckIcon className="h-4 w-4" data-id="element-9804" />}
                    </button>
                    {onDelete && <button onClick={() => onDelete(rate.id)} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-9805">
                        <TrashIcon className="h-4 w-4" data-id="element-9806" />
                      </button>}
                  </div>
                </td>
              </tr>)}
        </tbody>
      </table>
    </div>;
}
// Helper component for the arrow icon
const ArrowRightIcon = ({
  className
}: {
  className?: string;
}) => <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-9807">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" data-id="element-9808" />
  </svg>;